import React from 'react';
import Layout from '../components/Layout';

const sections = [
  {
    title: 'Chart of Accounts',
    icon: '📋',
    description: 'The chart of accounts is the foundation of your accounting system. Every transaction flows through an account.',
    steps: [
      'Go to Chart of Accounts in the sidebar.',
      'Click Add Account to create accounts like Cash (1000), Accounts Receivable (1200), Revenue (4000), etc.',
      'Assign each account a unique code, a name, and a type (Asset, Liability, Equity, Revenue, or Expense).',
      'Use the type filter to quickly find accounts by category.',
    ],
    tip: 'A common starting setup: 1000s for Assets, 2000s for Liabilities, 3000s for Equity, 4000s for Revenue, 5000s for Expenses. We\'ve already seeded the standard accounts for you — see the table below.',
  },
  {
    title: 'Journal Entries',
    icon: '📝',
    description: 'Journal entries record financial transactions using double-entry bookkeeping. Every entry must have equal debits and credits.',
    steps: [
      'Go to Journal Entries and click New Entry.',
      'Set the date, an optional reference number, and a description.',
      'Add at least two lines — one debit and one credit — selecting the appropriate accounts.',
      'The save button is disabled until debits equal credits.',
      'Use Draft status for entries you\'re still working on. Post them when they\'re final.',
    ],
    tip: 'Example: Recording a $500 sale — Debit Accounts Receivable $500, Credit Revenue $500.',
  },
  {
    title: 'Invoices',
    icon: '📄',
    description: 'Invoices track money owed to you by customers. Each invoice has line items tied to revenue accounts.',
    steps: [
      'Go to Invoices and click New Invoice.',
      'Enter an invoice number, select a contact and/or company, and set the issue and due dates.',
      'Add line items with an account, description, quantity, and unit price. The total calculates automatically.',
      'Set the status to Draft while preparing, then change to Sent when delivered to the customer.',
      'Mark as Paid once payment is received.',
    ],
    tip: 'Use the status filter on the Invoices list to quickly find overdue or unpaid invoices.',
  },
  {
    title: 'Bills',
    icon: '🧾',
    description: 'Bills track money you owe to vendors. They work just like invoices but in the opposite direction.',
    steps: [
      'Go to Bills and click New Bill.',
      'Enter the bill number from your vendor, select the vendor contact/company, and set dates.',
      'Add line items tied to expense accounts.',
      'Use Received status for bills you\'ve gotten but haven\'t paid yet.',
      'Mark as Paid once you\'ve sent payment.',
    ],
    tip: 'Keep an eye on the Accounts Payable total on the Dashboard to know how much you owe.',
  },
  {
    title: 'Payments',
    icon: '💳',
    description: 'Record payments against invoices and bills to track partial payments, outstanding balances, and payment history.',
    steps: [
      'Open any invoice or bill detail page.',
      'Click Record Payment to expand the payment form.',
      'Enter the amount, date, payment method (Cash, Check, Bank Transfer, Credit Card, or Other), and the deposit/payment account.',
      'Optionally add a reference number (e.g. check number) and notes.',
      'The Balance Due updates automatically after each payment.',
    ],
    tip: 'You can record multiple partial payments. The balance due will track what\'s still outstanding.',
  },
  {
    title: 'Recurring Transactions',
    icon: '🔄',
    description: 'Set up templates for transactions that repeat on a schedule — like monthly rent bills, subscription invoices, or recurring journal entries.',
    steps: [
      'Go to Recurring in the sidebar.',
      'Click New Recurring and give it a name.',
      'Choose the type (Invoice, Bill, or Journal Entry) and frequency (Weekly, Monthly, Quarterly, or Yearly).',
      'Set the next date it should generate, and optionally an end date.',
      'Paste the template JSON — this is the body that will be used to create the transaction.',
      'Toggle Active/Paused to control whether it generates.',
    ],
    tip: 'The template JSON should match the request body for the corresponding type. For example, an Invoice template should match the InvoiceRequest format.',
  },
  {
    title: 'Bank Reconciliation',
    icon: '🏦',
    description: 'Compare your bank statement against your books to catch discrepancies, missing transactions, and errors.',
    steps: [
      'Go to Bank Reconciliation in the sidebar.',
      'Click New Reconciliation and select the bank account (e.g. Cash 1000).',
      'Enter the statement date and the ending balance from your bank statement.',
      'Add each line from your bank statement — date, description, and amount.',
      'Check the Matched box for lines that match a journal entry in your books.',
      'The difference between the statement balance and matched total shows at the top — aim for zero.',
    ],
    tip: 'Reconcile monthly. If the difference isn\'t zero, look for missing journal entries, duplicate entries, or incorrect amounts.',
  },
  {
    title: 'General Ledger',
    icon: '📊',
    description: 'The general ledger shows all posted journal entry activity for a specific account, with a running balance.',
    steps: [
      'Go to General Ledger in the sidebar.',
      'Select an account and optionally set a date range.',
      'Click Run to see every posted transaction affecting that account.',
      'The running balance updates with each entry, so you can see the account balance at any point in time.',
    ],
    tip: 'Only posted journal entries appear in reports. Draft and void entries are excluded.',
  },
  {
    title: 'Trial Balance',
    icon: '⚖️',
    description: 'The trial balance summarizes all account balances as of a given date. Total debits should always equal total credits.',
    steps: [
      'Go to Trial Balance in the sidebar.',
      'Set an "as of" date (defaults to today).',
      'Click Run to see every account with its total debits and credits.',
      'If the totals don\'t match, a red difference row will appear — this means there\'s a data issue to investigate.',
    ],
    tip: 'Run a trial balance at the end of each month to verify your books are in balance.',
  },
];

export default function Info() {
  return (
    <Layout>
      <div className="max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">How to Use Accounting</h1>
          <p className="text-gray-600">A quick guide to get started with the accounting module. Work through the sections below in order — each one builds on the previous.</p>
        </div>

        <div className="space-y-6">
          {sections.map((section, i) => (
            <div key={i} className="bg-white rounded-lg shadow">
              <div className="p-4 md:p-6 border-b">
                <h2 className="text-xl font-semibold text-gray-900">
                  <span className="mr-2">{section.icon}</span>
                  {i + 1}. {section.title}
                </h2>
                <p className="text-gray-600 mt-1">{section.description}</p>
              </div>
              <div className="p-4 md:p-6">
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  {section.steps.map((step, j) => (
                    <li key={j}>{step}</li>
                  ))}
                </ol>
                <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-800"><span className="font-semibold">💡 Tip:</span> {section.tip}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-4 md:p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">
              <span className="mr-2">🏦</span>Pre-loaded Chart of Accounts
            </h2>
            <p className="text-gray-600 mt-1">These accounts are seeded automatically when the app is set up. You can edit, deactivate, or add new accounts at any time.</p>
          </div>
          <div className="p-4 md:p-6">
            {[
              { type: 'Assets', color: 'bg-blue-100 text-blue-800', accounts: [
                { code: '1000', name: 'Cash', desc: 'Cash on hand and in bank accounts' },
                { code: '1100', name: 'Accounts Receivable', desc: 'Money owed to you by customers' },
                { code: '1200', name: 'Inventory', desc: 'Goods held for sale' },
                { code: '1300', name: 'Prepaid Expenses', desc: 'Expenses paid in advance' },
                { code: '1500', name: 'Fixed Assets', desc: 'Equipment, furniture, etc.' },
                { code: '1510', name: 'Accumulated Depreciation', desc: 'Contra account for fixed assets' },
              ]},
              { type: 'Liabilities', color: 'bg-red-100 text-red-800', accounts: [
                { code: '2000', name: 'Accounts Payable', desc: 'Money you owe to vendors' },
                { code: '2100', name: 'Credit Card Payable', desc: 'Outstanding credit card balances' },
                { code: '2200', name: 'Accrued Liabilities', desc: 'Expenses incurred but not yet paid' },
                { code: '2300', name: 'Sales Tax Payable', desc: 'Sales tax collected but not yet remitted' },
                { code: '2400', name: 'Payroll Liabilities', desc: 'Wages and payroll taxes owed' },
                { code: '2500', name: 'Notes Payable', desc: 'Short-term and long-term loans' },
              ]},
              { type: 'Equity', color: 'bg-purple-100 text-purple-800', accounts: [
                { code: '3000', name: 'Owner\'s Equity', desc: 'Owner\'s investment in the business' },
                { code: '3100', name: 'Retained Earnings', desc: 'Accumulated profits not distributed' },
                { code: '3200', name: 'Owner\'s Draw', desc: 'Withdrawals by the owner' },
              ]},
              { type: 'Revenue', color: 'bg-green-100 text-green-800', accounts: [
                { code: '4000', name: 'Sales Revenue', desc: 'Income from selling goods or services' },
                { code: '4100', name: 'Service Revenue', desc: 'Income from services provided' },
                { code: '4200', name: 'Interest Income', desc: 'Income earned from interest' },
                { code: '4900', name: 'Other Income', desc: 'Miscellaneous income' },
              ]},
              { type: 'Expenses', color: 'bg-orange-100 text-orange-800', accounts: [
                { code: '5000', name: 'Cost of Goods Sold', desc: 'Direct costs of producing goods sold' },
                { code: '5100', name: 'Advertising & Marketing', desc: 'Costs for advertising and marketing' },
                { code: '5200', name: 'Bank & Merchant Fees', desc: 'Bank charges and payment processing fees' },
                { code: '5300', name: 'Insurance', desc: 'Business insurance premiums' },
                { code: '5400', name: 'Office Supplies', desc: 'Supplies used in the office' },
                { code: '5500', name: 'Payroll Expense', desc: 'Wages and salaries' },
                { code: '5600', name: 'Rent Expense', desc: 'Rent for office or business space' },
                { code: '5700', name: 'Utilities', desc: 'Electricity, water, internet, phone' },
                { code: '5800', name: 'Depreciation Expense', desc: 'Periodic depreciation of fixed assets' },
                { code: '5900', name: 'Professional Services', desc: 'Legal, accounting, consulting fees' },
                { code: '5950', name: 'Travel & Meals', desc: 'Business travel and meal expenses' },
                { code: '5990', name: 'Miscellaneous Expense', desc: 'Other uncategorized expenses' },
              ]},
            ].map((group, i) => (
              <div key={i} className={i > 0 ? 'mt-6' : ''}>
                <h3 className="font-semibold text-gray-900 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${group.color} mr-2`}>{group.type}</span>
                  {group.type} (codes {group.accounts[0].code}–{group.accounts[group.accounts.length - 1].code})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {group.accounts.map((a, j) => (
                        <tr key={j}>
                          <td className="px-4 py-2 font-mono text-sm text-gray-900">{a.code}</td>
                          <td className="px-4 py-2 font-medium text-gray-900">{a.name}</td>
                          <td className="px-4 py-2 text-gray-500 text-sm hidden md:table-cell">{a.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-800"><span className="font-semibold">💡 Tip:</span> Add your own accounts in the gaps between codes. For example, use 1400 for a new asset account between Prepaid Expenses (1300) and Fixed Assets (1500).</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-gray-50 rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Recommended Workflow</h2>
          <div className="flex flex-col sm:flex-row gap-3 text-sm text-gray-700 flex-wrap">
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 shadow-sm flex-1 text-center justify-center min-w-[140px]">
              <span>📋</span> Set up accounts
            </div>
            <div className="hidden sm:flex items-center text-gray-400">→</div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 shadow-sm flex-1 text-center justify-center min-w-[140px]">
              <span>📄</span> Create invoices & bills
            </div>
            <div className="hidden sm:flex items-center text-gray-400">→</div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 shadow-sm flex-1 text-center justify-center min-w-[140px]">
              <span>💳</span> Record payments
            </div>
            <div className="hidden sm:flex items-center text-gray-400">→</div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 shadow-sm flex-1 text-center justify-center min-w-[140px]">
              <span>📝</span> Record journal entries
            </div>
            <div className="hidden sm:flex items-center text-gray-400">→</div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 shadow-sm flex-1 text-center justify-center min-w-[140px]">
              <span>🏦</span> Reconcile bank
            </div>
            <div className="hidden sm:flex items-center text-gray-400">→</div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 shadow-sm flex-1 text-center justify-center min-w-[140px]">
              <span>⚖️</span> Run reports
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
